import { motion } from "framer-motion";
import TypeAnimation from "react-type-animation";
import { FaCode, FaProjectDiagram, FaStar, FaBriefcase } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { BottomLine, MySkill, Education } from "../../components";
import simraImage from "../../assets/simraaa.png";
import "./About.css";
import "../../components/atoms/PrimaryBtn/PrimaryBtn.css";
import "../shared/Shared.css";

const About = () => {

  const stats = [
    { icon: <FaProjectDiagram />, value: "50+", label: "Projects" },
    { icon: <FaCode />,           value: "3+",  label: "Yrs Exp"  },
    { icon: <FaStar />,           value: "98%", label: "Satisfaction" },
  ];

  return (
    <div className="relative overflow-hidden text-white bg-[#0c0a16] w-full">

      <div className="relative w-full pt-24 pb-16 sm:pt-28 sm:pb-20">

        {/* Background blobs */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-violet-700/15 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-indigo-700/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10">

          {/* Section Heading */}
          <motion.div
            className="mb-10 md:mb-14 text-center"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { duration: 0.8, type: "spring" } }}
          >
            <h1 className="text-3xl md:text-4xl font-semibold mb-2">
              About <span className="text-violet-400">Me</span>
            </h1>
            <h3 className="text-gray-400 text-base tracking-wide">Something About Myself</h3>
            <BottomLine />
          </motion.div>

          {/* Main Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">

            {/* LEFT — Image */}
            <motion.div
              initial={{ x: -60, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { duration: 0.9, delay: 0.2 } }}
              className="flex justify-center"
            >
              <div className="relative inline-block">

                <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-violet-600/40 via-indigo-500/30 to-violet-600/40 blur-xl z-0" />

                <div className="relative z-10 p-[3px] rounded-3xl bg-gradient-to-br from-violet-500 via-indigo-400 to-violet-600 shadow-2xl">
                  <div className="rounded-3xl overflow-hidden bg-[#0c0a16]">
                    <img
                      src={simraImage}
                      alt="Simra Malik"
                      className="w-56 h-64 sm:w-64 sm:h-72 md:w-72 md:h-80 lg:w-80 lg:h-96 object-cover object-[50%_15%] block"
                      title="Simra Malik"
                    />
                  </div>
                </div>

                {/* Available badge */}
                <div className="absolute -top-3 -right-3 z-20 flex items-center gap-1.5
                                bg-[#0c0a16] border border-green-500/40 rounded-full px-3 py-1.5 shadow-lg">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs font-semibold text-green-300 whitespace-nowrap">Available</span>
                </div>

              </div>
            </motion.div>

            {/* RIGHT — Info */}
            <motion.div
              initial={{ x: 60, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { duration: 0.9, delay: 0.4 } }}
              className="flex flex-col text-center md:text-left"
            >
              {/* Label */}
              <div className="inline-flex items-center gap-2 bg-violet-500/15 border border-violet-500/30
                              rounded-full px-4 py-1.5 mb-4 self-center md:self-start">
                <HiSparkles className="text-violet-400 text-sm" />
                <span className="text-violet-300 text-xs font-semibold tracking-wider uppercase">
                  Full Stack Developer
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3 leading-tight
                             bg-gradient-to-r from-white via-violet-200 to-indigo-200 bg-clip-text text-transparent">
                Simra Malik
              </h1>

              <div className="h-8 mb-4 overflow-hidden">
                <TypeAnimation
                  className="text-base sm:text-lg text-violet-300 font-semibold whitespace-nowrap"
                  cursor={true}
                  sequence={[
                    "Full-Stack Web & Mobile Developer", 2000,
                    "React & Next.js Specialist",        2000,
                    "Tailwind & UI/UX Enthusiast",       2000,
                    "Node.js & Express Pro",             2000,
                    "MongoDB & Database Explorer",       2000,
                    "Performance & Optimization Lover",  2000,
                  ]}
                  wrapper="div"
                  repeat={Infinity}
                />
              </div>

              <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-3">
                As a Full-Stack Developer, I build complete web applications with modern
                technologies combining creative frontend design with robust backend systems
                to deliver scalable, high-performance solutions.
              </p>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6">
                From concept to deployment, I ensure every application is performant,
                maintainable, and delivers exceptional user experiences across the full stack.
              </p>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {stats.map((s, i) => (
                  <div key={i}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl
                               bg-white/5 border border-violet-500/20 hover:border-violet-500/40
                               transition-colors duration-300">
                    <span className="text-violet-400 text-base">{s.icon}</span>
                    <span className="text-white font-black text-lg leading-none">{s.value}</span>
                    <span className="text-gray-400 text-xs">{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Nexora badge */}
              <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl
                              bg-white/5 border border-violet-500/20 mb-6
                              hover:border-violet-500/35 transition-colors duration-300">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-violet-700
                                flex items-center justify-center flex-shrink-0 shadow-lg">
                  <FaBriefcase className="text-white text-sm" />
                </div>
                <div className="flex-grow">
                  <p className="text-white font-bold text-sm sm:text-base leading-none mb-0.5">Nexora</p>
                  <p className="text-gray-400 text-xs sm:text-sm">Senior Full Stack Developer</p>
                </div>
                <div className="flex items-center gap-1.5 bg-green-500/15 border border-green-500/30
                                rounded-full px-2.5 py-1 flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-300 text-xs font-medium">Present</span>
                </div>
              </div>

            </motion.div>
          </div>

        </div>
      </div>

      <MySkill />
      <Education />

    </div>
  );
};

export default About;
